import {
  ChatBotUIProduct,
  ChatBotUIProductVariant,
} from "@jugl-web/domain-resources/chat-bot/types";
import { getProductPriceText } from "@jugl-web/domain-resources/chat-bot/utils/getProductPriceText";
import { cx, useTranslations } from "@jugl-web/utils";
import { useIsMediumOrAbove } from "@jugl-web/utils/hooks/useMediaQuery";
import { ErrorBoundary } from "@jugl-web/utils/utils/ErrorBoundary";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { ChatButton } from "../ChatButton";
import { ChatControlButton } from "../ChatControlButton";
import { ChatDrawer } from "../ChatDrawer";
import { ChatHeaderContainer } from "../ChatHeaderContainer";
import { ProductCard } from "../ProductCard";
import { ProductQuantitySelect } from "../ProductQuantitySelect";
import { ProductVariantSelect } from "../ProductVariantSelect";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { ReactComponent as CrossIcon } from "./assets/cross.svg";

interface ChatProductDetailsDrawerProps {
  isOpen: boolean;
  product: ChatBotUIProduct | null;
  positionStrategy?: "fixed" | "absolute";
  layoutMode?: "sidebar" | "fullscreen";
  onAddToCart: (
    product: ChatBotUIProduct,
    variant: ChatBotUIProductVariant,
    quantity: number
  ) => void;
  onClose: () => void;
}

export const ChatProductDetailsDrawer: FC<ChatProductDetailsDrawerProps> = ({
  isOpen,
  product,
  positionStrategy,
  layoutMode,
  onAddToCart,
  onClose,
}) => {
  const isMediumOrAbove = useIsMediumOrAbove();

  const resolvedPositionStrategy =
    positionStrategy ?? (isMediumOrAbove ? "fixed" : "absolute");

  const resolvedLayoutMode =
    layoutMode ?? (isMediumOrAbove ? "sidebar" : "fullscreen");

  return (
    <ChatDrawer
      isOpen={isOpen}
      placement="right"
      positionStrategy={resolvedPositionStrategy}
      panelClassName={
        resolvedLayoutMode === "fullscreen" ? "w-full" : "w-full max-w-[500px]"
      }
      onClose={onClose}
    >
      <ErrorBoundary fallback={<ErrorBoundaryFallback onClose={onClose} />}>
        {product && (
          <ChatProductDetailsDrawerContent
            key={product.id}
            isOpen={isOpen}
            product={product}
            layoutMode={resolvedLayoutMode}
            onAddToCart={onAddToCart}
            onClose={onClose}
          />
        )}
      </ErrorBoundary>
    </ChatDrawer>
  );
};

type ChatProductDetailsDrawerContentProps = Omit<
  ChatProductDetailsDrawerProps,
  "positionStrategy"
> & {
  product: ChatBotUIProduct;
};

interface ProductVariantOptionSet {
  name: string;
  values: string[];
}

const ChatProductDetailsDrawerContent: FC<
  ChatProductDetailsDrawerContentProps
> = ({ isOpen, product, layoutMode, onAddToCart, onClose }) => {
  const [quantity, setQuantity] = useState(1);

  const [selectedVariantId, setSelectedVariantId] = useState<string>(
    product.variants[0].id
  );

  const isSidebarLayout = layoutMode === "sidebar";

  const { t } = useTranslations();

  const selectedVariant = useMemo(() => {
    const matchingVariant = product.variants.find(
      (variant) => variant.id === selectedVariantId
    );

    if (!matchingVariant) {
      throw new Error("Selected variant not found");
    }

    return matchingVariant;
  }, [product.variants, selectedVariantId]);

  const findCompatibleVariant = useCallback(
    (optionName: string, optionValue: string) =>
      product.variants.find((variant) => {
        const hasSelectedOption = variant.selectedOptions.some(
          (option) => option.name === optionName && option.value === optionValue
        );

        if (!hasSelectedOption) {
          return false;
        }

        const matchesSelectedVariantOptions =
          selectedVariant.selectedOptions.every((selectedOption) => {
            if (selectedOption.name === optionName) {
              return true;
            }

            return variant.selectedOptions.some(
              (option) =>
                option.name === selectedOption.name &&
                option.value === selectedOption.value
            );
          });

        return matchesSelectedVariantOptions;
      }),
    [product.variants, selectedVariant.selectedOptions]
  );

  const optionSets = useMemo<ProductVariantOptionSet[]>(() => {
    const optionsByName = new Map<string, Set<string>>();

    product.variants.forEach((variant) => {
      variant.selectedOptions.forEach((option) => {
        const values = optionsByName.get(option.name) ?? new Set<string>();

        values.add(option.value);
        optionsByName.set(option.name, values);
      });
    });

    return Array.from(optionsByName, ([name, values]) => ({
      name,
      values: Array.from(values),
    }));
  }, [product.variants]);

  const totalPrice = useMemo<number | null>(() => {
    if (selectedVariant.price === null) {
      return null;
    }

    return selectedVariant.price * quantity;
  }, [quantity, selectedVariant.price]);

  const handleSelectOption = (optionName: string, optionValue: string) => {
    const nextVariant = findCompatibleVariant(optionName, optionValue);

    if (nextVariant) {
      setSelectedVariantId(nextVariant.id);
    }
  };

  const handleAddToCart = () => {
    onAddToCart(product, selectedVariant, quantity);
    onClose();
  };

  // Reset selected variant and quantity when the product changes
  useEffect(() => {
    setSelectedVariantId(product.variants[0].id);
    setQuantity(1);
  }, [product]);

  // Reset quantity when the available quantity of the selected variant is less than the current quantity
  useEffect(() => {
    if (
      selectedVariant.availableQuantity === null ||
      selectedVariant.availableQuantity >= quantity
    ) {
      return;
    }

    setQuantity(1);
  }, [quantity, selectedVariant]);

  // Reset quantity when the drawer is closed
  useEffect(() => {
    if (!isOpen) {
      setQuantity(1);
    }
  }, [isOpen]);

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <ChatHeaderContainer className={cx(isSidebarLayout && "px-6")}>
        <div
          className={cx(
            "flex w-full items-center gap-2",
            isSidebarLayout && "justify-between"
          )}
        >
          {!isSidebarLayout && (
            <ChatControlButton size="md" onClick={onClose}>
              <ArrowIcon />
            </ChatControlButton>
          )}
          <span
            className={cx(
              "font-secondary text-dark truncate",
              !isSidebarLayout
                ? "text-[15px] font-medium"
                : "text-base font-semibold"
            )}
          >
            {t({
              id: "chat-bot-product-details-drawer.item-details",
              defaultMessage: "Item details",
            })}
          </span>
          {isSidebarLayout && (
            <ChatControlButton size="md" onClick={onClose}>
              <CrossIcon />
            </ChatControlButton>
          )}
        </div>
      </ChatHeaderContainer>
      {/* Scrollable content */}
      <div
        className={cx(
          "jugl__custom-scrollbar flex-1 shrink-0 overflow-y-auto",
          !isSidebarLayout ? "px-3 py-4" : "p-8"
        )}
      >
        <ProductCard
          name={`${product.title} - ${selectedVariant.title}`}
          imageUrl={selectedVariant.imageUrl}
          priceText={getProductPriceText(
            selectedVariant.price,
            product.currencyCode
          )}
        />
        <div className="mt-6 flex flex-col gap-4">
          <ProductQuantitySelect
            quantity={quantity}
            maxQuantity={selectedVariant.availableQuantity}
            onQuantityChange={setQuantity}
          />
          {optionSets.map((option) => {
            const selectedOption = selectedVariant.selectedOptions.find(
              (o) => o.name === option.name
            )?.value;

            return (
              <ProductVariantSelect
                key={option.name}
                label={option.name}
                options={option.values.map((value) => {
                  const isAvailable = Boolean(
                    findCompatibleVariant(option.name, value)
                  );

                  return {
                    id: value,
                    label: value,
                    isDisabled: !isAvailable,
                  };
                })}
                selectedOption={selectedOption ?? null}
                onSelect={(value) => handleSelectOption(option.name, value)}
              />
            );
          })}
        </div>
      </div>
      {/* Footer */}
      <div
        className={cx(
          "flex shrink-0 flex-col gap-4 border-b-0 border-l-0 border-r-0 border-t border-solid border-[#F2F2F2]",
          !isSidebarLayout ? "px-3 pb-6 pt-4" : "px-8 py-8"
        )}
      >
        <div className="flex shrink-0 items-center justify-between gap-2 px-3">
          <span className="font-secondary text-dark text-sm font-normal leading-[150%]">
            {t({
              id: "chat-bot-product-details-drawer.total",
              defaultMessage: "Total",
            }).concat(":")}
          </span>
          <span className="font-secondary text-dark text-base font-semibold leading-[150%]">
            {getProductPriceText(totalPrice, product.currencyCode)}
          </span>
        </div>
        <ChatButton
          variant="primary"
          fullWidth
          className="shrink-0"
          isDisabled={quantity < 1}
          onClick={handleAddToCart}
        >
          {t({
            id: "chat-bot-product-details-drawer.add-to-cart",
            defaultMessage: "Add to cart",
          })}
        </ChatButton>
      </div>
    </div>
  );
};

const ErrorBoundaryFallback = ({ onClose }: { onClose: () => void }) => (
  <div className="flex h-full w-full flex-col items-center justify-center gap-4">
    <span className="font-secondary text-dark text-sm font-normal leading-[150%]">
      We couldn&apos;t load the product details.
    </span>
    <ChatButton variant="primary" size="sm" className="px-6" onClick={onClose}>
      Go back
    </ChatButton>
  </div>
);
